/**
 * PreferencesPanel — Learned Preferences Viewer/Editor (Tier 3)
 * 
 * Shows all replica-learned preferences grouped by category.
 * Users can view confidence, learning count, and deactivate.
 * Integrates into CognitiveLab as a tab.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface Preference {
  id: string;
  ruleKey: string;
  ruleValue: any;
  confidence: number;
  learnedFrom: number;
  scope: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  replicaId: string;
  getToken: () => Promise<string>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const CATEGORY_ICONS: Record<string, string> = {
  'email_drafting': '✉️',
  'meeting_scheduling': '📅',
  'task_prioritization': '📊',
  'document_review': '📝',
  'communication_style': '💬',
  'content_creation': '🖊️',
  'financial_decisions': '💰',
  'data_analysis': '📈',
  'negotiation': '🤝',
  'code_review': '💻',
};

function confidenceColor(c: number): string {
  if (c >= 0.8) return '#22c55e';
  if (c >= 0.5) return '#f59e0b';
  return '#ef4444';
}

function confidenceLabel(c: number): string {
  if (c >= 0.8) return 'Strong';
  if (c >= 0.5) return 'Moderate';
  if (c >= 0.3) return 'Weak';
  return 'Fading';
}

export const PreferencesPanel: React.FC<Props> = ({ replicaId, getToken }) => {
  const [grouped, setGrouped] = useState<Record<string, Preference[]>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);

  const loadPreferences = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/preferences/${replicaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setGrouped(data.preferences || {});
        setTotalCount(data.totalPreferences || 0);
      }
    } catch (e) {
      console.error('Failed to load preferences:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, getToken]);

  useEffect(() => { loadPreferences(); }, [loadPreferences]);

  const deactivate = async (prefId: string) => {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/api/v1/preferences/${replicaId}/${prefId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadPreferences();
    } catch (e) {
      console.error('Deactivation failed:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categories = Object.keys(grouped);

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">🧠</span>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Learned Preferences</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {totalCount} active preferences · PLANNING scope only (governance-protected)
            </p>
          </div>
        </div>

        {/* ── Empty State ── */}
        {categories.length === 0 && (
          <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-12 text-center">
            <span className="text-4xl block mb-3">🧠</span>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
              No preferences learned yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Your replica learns preferences from accepted/rejected actions.
              Keep interacting — it will pick up your patterns automatically.
            </p>
          </div>
        )}

        {/* ── Category Groups ── */}
        {categories.map(category => {
          const prefs = grouped[category];
          const icon = CATEGORY_ICONS[category.split('.')[0]] || '⚙️';
          const isExpanded = expandedCat === category;
          const avgConfidence = prefs.reduce((s, p) => s + p.confidence, 0) / prefs.length;

          return (
            <div
              key={category}
              className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => setExpandedCat(isExpanded ? null : category)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{icon}</span>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white capitalize">
                      {category.replace(/[._]/g, ' ')}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {prefs.length} rule{prefs.length !== 1 ? 's' : ''} · Avg confidence{' '}
                      <span style={{ color: confidenceColor(avgConfidence) }}>
                        {Math.round(avgConfidence * 100)}%
                      </span>
                    </p>
                  </div>
                </div>
                <span className="text-slate-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
              </button>

              {isExpanded && (
                <div className="border-t border-slate-200 dark:border-slate-700/50 divide-y divide-slate-100 dark:divide-slate-700/30">
                  {prefs.map(pref => (
                    <div key={pref.id} className="p-4 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-xs bg-slate-100 dark:bg-slate-700/50 px-1.5 py-0.5 rounded text-indigo-600 dark:text-indigo-400 font-mono">
                            {pref.ruleKey}
                          </code>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full border"
                            style={{
                              color: confidenceColor(pref.confidence),
                              borderColor: confidenceColor(pref.confidence) + '40',
                              backgroundColor: confidenceColor(pref.confidence) + '15',
                            }}
                          >
                            {confidenceLabel(pref.confidence)} · {Math.round(pref.confidence * 100)}%
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 break-all">
                          {typeof pref.ruleValue === 'object'
                            ? JSON.stringify(pref.ruleValue, null, 1)
                            : String(pref.ruleValue)}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                          <span>Learned from {pref.learnedFrom} observation{pref.learnedFrom !== 1 ? 's' : ''}</span>
                          <span>·</span>
                          <span>Scope: {pref.scope}</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => { e.stopPropagation(); deactivate(pref.id); }}
                        className="shrink-0 text-[10px] px-2 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800/40 transition-colors"
                      >
                        Deactivate
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Guardrail Notice ── */}
        {categories.length > 0 && (
          <div className="bg-amber-50/80 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <span className="text-sm">🛡️</span>
              <div>
                <h4 className="text-xs font-semibold text-amber-800 dark:text-amber-400 mb-1">
                  Governance Protection
                </h4>
                <p className="text-[11px] text-amber-700 dark:text-amber-500 leading-relaxed">
                  All preferences operate in <strong>PLANNING</strong> scope only. They cannot bypass the Write Barrier,
                  Draft-First enforcement, Trust Gating, or any governance guardrails. Deactivating a preference
                  is immediate and permanent.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreferencesPanel;
